import axios from 'axios';
import { API_CONFIG } from '../utils/constants';
import { getBrowserSecurityHeaders, clearDeviceId } from '../utils/browserFingerprint';
import { appNavigate } from '../utils/navigationRef';

// Helper function to get CSRF token from cookies
const getCsrfToken = (): string | null => {
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'csrf_token') {
      return decodeURIComponent(value);
    }
  }
  return null;
};

// Helper function to check if token is expired or expiring soon
const isTokenExpiringSoon = (): boolean => {
  const tokenExpiresAt = localStorage.getItem('tokenExpiresAt');
  if (!tokenExpiresAt) {
    return false;
  }

  const expiryTime = parseInt(tokenExpiresAt, 10);
  const now = Date.now();
  const timeUntilExpiry = expiryTime - now;
  const FIVE_MINUTES = 5 * 60 * 1000;

  return timeUntilExpiry < FIVE_MINUTES;
};

// Track ongoing refresh to prevent multiple concurrent refreshes
let ongoingRefresh: Promise<string> | null = null;

const refreshTokenIfNeeded = async (): Promise<string | null> => {
  const token = localStorage.getItem('authToken');
  
  if (!token) {
    return null;
  }

  if (!isTokenExpiringSoon()) {
    return token;
  }

  // If refresh is already happening, wait for it
  if (ongoingRefresh) {
    console.log('⏳ [authClient] Refresh in progress, waiting...');
    return ongoingRefresh;
  }

  console.log('🔄 [authClient] Token expiring soon (<5 min), proactively refreshing...');
  
  ongoingRefresh = (async () => {
    try {
      const refreshResponse = await authClient.post('/auth/refresh');
      const newToken = refreshResponse.data.access_token;
      const expiresIn = refreshResponse.data.expires_in;
      
      localStorage.setItem('authToken', newToken);
      const expiresAt = Date.now() + (expiresIn * 1000);
      localStorage.setItem('tokenExpiresAt', expiresAt.toString());
      
      console.log('✅ [authClient] Token refreshed proactively');
      ongoingRefresh = null;
      return newToken;
    } catch (error) {
      console.error('❌ [authClient] Proactive refresh failed:', error);
      ongoingRefresh = null;
      
      localStorage.removeItem('authToken');
      localStorage.removeItem('tokenExpiresAt');
      clearDeviceId();
      appNavigate('/login', { replace: true });
      throw error;
    }
  })();

  return ongoingRefresh;
};

// Create dedicated axios instance for authentication service
const authClient = axios.create({
  baseURL: API_CONFIG.AUTH_BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Important for refresh token cookies
});

// Request interceptor with proactive token refresh
authClient.interceptors.request.use(
  async (config) => {
    // Skip token refresh for login/refresh endpoints
    const skipRefreshEndpoints = ['/auth/verify-code', '/auth/refresh', '/auth/request-code'];
    const shouldSkipRefresh = skipRefreshEndpoints.some(endpoint => 
      config.url?.includes(endpoint)
    );

    if (!shouldSkipRefresh) {
      // Proactively refresh if expiring soon
      const token = await refreshTokenIfNeeded();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } else {
      // For login/refresh endpoints, just add token if available
      const token = localStorage.getItem('authToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    // Add CSRF token for protected endpoints
    const protectedEndpoints = ['/auth/refresh', '/auth/logout', '/auth/logout-all'];
    const isProtectedEndpoint = protectedEndpoints.some(endpoint => 
      config.url?.includes(endpoint)
    );
    
    if (isProtectedEndpoint) {
      const csrfToken = getCsrfToken();
      if (csrfToken) {
        config.headers['X-CSRF-Token'] = csrfToken;
        console.log('🔒 Adding CSRF token to request:', config.url);
      } else {
        console.warn('⚠️ No CSRF token found for protected endpoint:', config.url);
      }
    }

    // Add browser security headers for browser-only API access
    const browserHeaders = getBrowserSecurityHeaders();
    Object.entries(browserHeaders).forEach(([key, value]) => {
      config.headers[key] = value;
    });

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor with reactive refresh (fallback)
authClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle session invalidated due to new device login
    if (error.response?.data?.reason === 'session_invalidated_new_device' ||
        error.response?.data?.error === 'session_invalidated_new_device') {
      console.warn('🔒 [authClient] Session invalidated - logged in from another device');
      
      // Clear all auth data
      localStorage.removeItem('authToken');
      localStorage.removeItem('tokenExpiresAt');
      clearDeviceId();
      
      // Dispatch custom event for UI to show modal
      window.dispatchEvent(new CustomEvent('session-invalidated', {
        detail: { reason: 'new_device' }
      }));
      
      return Promise.reject(error);
    }

    // Handle CSRF token missing/expired (403 errors)
    if (error.response?.status === 403 && 
        error.response?.data?.error?.includes('CSRF')) {
      console.error('🔒 CSRF token validation failed:', error.response.data);
      
      if (originalRequest.url?.includes('/auth/refresh')) {
        console.log('🔄 CSRF failed on refresh, redirecting to login');
        localStorage.removeItem('authToken');
        localStorage.removeItem('tokenExpiresAt');
        clearDeviceId();
        appNavigate('/login', { replace: true });
      }
      
      return Promise.reject(error);
    }

    // Handle token refresh for 401 errors (reactive fallback)
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auth/verify-code') &&
      !originalRequest.url?.includes('/auth/refresh') &&
      !originalRequest.url?.includes('/auth/request-code')
    ) {
      originalRequest._retry = true;

      try {
        console.log('🔄 [authClient] Token expired (401), attempting refresh...');
        
        const refreshResponse = await authClient.post('/auth/refresh');
        const newToken = refreshResponse.data.access_token;
        const expiresIn = refreshResponse.data.expires_in;
        
        localStorage.setItem('authToken', newToken);
        const expiresAt = Date.now() + (expiresIn * 1000);
        localStorage.setItem('tokenExpiresAt', expiresAt.toString());
        
        console.log('✅ [authClient] Token refreshed successfully');
        
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return authClient(originalRequest);
      } catch (refreshError) {
        console.error('❌ [authClient] Token refresh failed:', refreshError);
        
        localStorage.removeItem('authToken');
        localStorage.removeItem('tokenExpiresAt');
        clearDeviceId();
        appNavigate('/login', { replace: true });
        return Promise.reject(refreshError);
      }
    }

    // Log error for debugging
    console.error('Auth API Error:', {
      status: error.response?.status,
      data: error.response?.data,
      url: error.config?.url,
      method: error.config?.method
    });
    
    return Promise.reject(error);
  }
);

export default authClient;
